import { useState, useEffect } from 'react';
import { getCompanyWells } from '../utils/firebase/database';
import { getCompanyId, getUserCompanyId, getCompanyStorageKey } from '../utils/tenantContext';
import { indexedDBService } from '../utils/offline/indexedDB';

/**
 * useWells Hook - Load wells with tenant isolation
 * 🔒 SECURITY: Only loads wells for user's assigned company
 * 
 * @param userId - User ID to trigger reload on login
 * @param user - Full user object for tenant validation
 */
export function useWells(userId?: string, user?: any) {
  const [wells, setWells] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [backendConnected, setBackendConnected] = useState(false);

  useEffect(() => {
    // 🔒 SECURITY: Validate user and tenant before loading
    if (!userId || !user) {
      setLoading(false);
      setWells([]);
      return;
    }

    const companyId = getCompanyId();
    const userCompanyId = getUserCompanyId();

    // 🔒 SECURITY: SuperAdmin needs to select a company first
    if (user.role === 'superadmin' || user.isSuperAdmin === true) {
      setLoading(false);
      setWells([]);
      return;
    }

    // 🔒 SECURITY: Verify user has valid company assignment
    if (!userCompanyId || userCompanyId === 'default') {
      console.error('❌ User has no valid company assignment:', user.email);
      setLoading(false);
      setWells([]);
      return;
    }

    // 🔒 SECURITY: Verify companyId matches user's company
    if (companyId !== userCompanyId) {
      console.error('🚫 SECURITY WARNING: companyId mismatch!');
      console.error('   Current companyId:', companyId);
      console.error('   User companyId:', userCompanyId);
      console.error('   User email:', user.email);
      setLoading(false);
      setWells([]);
      return;
    }

    
    loadWells();
  }, [userId, user?.companyId, user?.companyId]); // Re-run when user or company changes

  const loadWells = async () => {
    setLoading(true);
    
    try {
      const companyId = getCompanyId();
      const userCompanyId = getUserCompanyId();

      // 🔒 FINAL SECURITY CHECK before API call
      if (!userCompanyId || userCompanyId === 'default') {
        console.error('❌ No valid company ID - cannot load wells');
        setWells([]);
        setLoading(false);
        return;
      }

      if (companyId !== userCompanyId) {
        console.error('🚫 Tenant mismatch - blocking well load');
        setWells([]);
        setLoading(false);
        return;
      }

      
      // 🔒 Load from Firebase Firestore with user validation
      const result = await getCompanyWells(companyId, user);
      
      if (Array.isArray(result)) {
        // Success - save to tenant-scoped localStorage and update state
        setWells(result);
        setBackendConnected(true);
        
        // Sync to tenant-scoped localStorage for offline use
        localStorage.setItem(getCompanyStorageKey('wells:data'), JSON.stringify(result));
        localStorage.setItem(getCompanyStorageKey('wells:lastSync'), new Date().toISOString());
        localStorage.setItem(getCompanyStorageKey('wells:company'), userCompanyId); // Track company for cache validation
        
        // Also sync to IndexedDB for more reliable offline storage (includes tank configs)
        try {
          if (!indexedDBService.isInitialized()) {
            await indexedDBService.initialize();
          }
          await indexedDBService.saveWells(result);
        } catch (idbErr) {
          console.warn('⚠️ IndexedDB cache failed, localStorage backup active:', idbErr);
        }
        
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error: any) {
      console.error('⚠️ Firebase not available:', error?.message || 'Unknown error');
      setBackendConnected(false);
      
      const userCompanyId = getUserCompanyId();
      let cachedWells: any[] | null = null;
      
      // 🔒 First try IndexedDB (more reliable for larger data like tank configs)
      try {
        if (!indexedDBService.isInitialized()) {
          await indexedDBService.initialize();
        }
        const idbWells = await indexedDBService.getWells();
        if (idbWells && idbWells.length > 0) {
          // Filter by company for security
          cachedWells = idbWells.filter(well => {
            const wellTenant = well.companyId;
            return !wellTenant || wellTenant === userCompanyId;
          });
          console.log('📦 Loaded wells from IndexedDB (offline):', cachedWells.length);
        }
      } catch (idbErr) {
        console.warn('⚠️ IndexedDB read failed, trying localStorage:', idbErr);
      }
      
      // 🔒 Fallback to localStorage if IndexedDB failed or empty
      if (!cachedWells || cachedWells.length === 0) {
        const lsCachedWells = JSON.parse(localStorage.getItem(getCompanyStorageKey('wells:data')) || 'null') as any[];
        const cachedCompany = localStorage.getItem(getCompanyStorageKey('wells:company'));
        
        // 🔒 SECURITY: Verify cached data is for correct company
        if (cachedCompany && cachedCompany !== userCompanyId) {
          localStorage.removeItem(getCompanyStorageKey('wells:data'));
          localStorage.removeItem(getCompanyStorageKey('wells:company'));
          localStorage.removeItem(getCompanyStorageKey('wells:lastSync'));
          cachedWells = [];
        } else if (lsCachedWells && Array.isArray(lsCachedWells)) {
          // Additional filter for safety
          cachedWells = lsCachedWells.filter(well => {
            const wellTenant = well.companyId;
            return !wellTenant || wellTenant === userCompanyId;
          });
          console.log('📦 Loaded wells from localStorage (offline):', cachedWells.length);
        }
      }
      
      if (cachedWells && cachedWells.length > 0) {
        setWells(cachedWells);
      } else {
        // No cached data - set empty array
        setWells([]);
      }
    }
    
    setLoading(false);
  };

  const refreshWells = () => {
    setLoading(true);
    loadWells();
  };

  const updateWell = async (wellId: string, updates: any) => {
    const companyId = getCompanyId();
    
    // Update local state immediately for instant UI feedback
    const updatedWells = wells.map(well => 
      well.id === wellId 
        ? { ...well, ...updates }
        : well
    );
    
    setWells(updatedWells);
    
    // Also update tenant-scoped localStorage
    localStorage.setItem(getCompanyStorageKey('wells:data'), JSON.stringify(updatedWells));
    localStorage.setItem(getCompanyStorageKey('wells:lastSync'), new Date().toISOString());

    // Save to Firebase
    try {
      const { saveWellDocument } = await import('../utils/firebase/database');
      await saveWellDocument(companyId, updates, wellId);
    } catch (error) {
      console.error('⚠️ Error updating well in Firebase:', error);
    }
  };

  return {
    wells,
    loading,
    backendConnected,
    refreshWells,
    updateWell,
  };
}
